import { styled } from 'styled-components';
import { Input } from 'antd';
import React, { ChangeEvent, CSSProperties, useCallback, useMemo, useRef, useState } from 'react';
import {
  ConfigurationFieldStatus,
  getColorBasedOnStatusAndOverwrite,
  StyledFieldProps,
} from '@ydistri/utils';
import {
  getIconBasedOnStatusAndOverwrite,
  OverwrittenIcon,
} from '../getIconBasedOnStatusAndOverwrite';
import { computeRemSize, STYLE } from '@ydistri/theme';

const StyledInput = styled(Input)<StyledFieldProps>`
  width: ${p => p.$width ?? STYLE.DEFAULT_INPUT_WIDTH};
  border-color: ${p => p.$color ?? undefined};
  color: ${p => p.$color ?? undefined};
  appearance: textfield;
  font-size: ${computeRemSize(14)};

  &::placeholder {
    color: black;
  }

  &::placeholder,
  &:disabled {
    color: grey;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input {
    font-size: 0.8rem;
    appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
`;

export interface ConfigurationInputProps {
  disabled?: boolean;
  /** indicates that the selected value overwrites a value set somewhere higher in the hierarchy */
  overwritten?: boolean;
  overwrittenIcon?: OverwrittenIcon;
  parentException?: boolean;
  status: ConfigurationFieldStatus;
  value: number;
  onChange?: (value: string) => void;
  width?: CSSProperties['width'];
  placeholder?: string;
  customSuffix?: React.ReactNode;
  displayZero?: boolean;
  allowDecimals?: boolean;
  min?: number;
  max?: number;
}

export const ConfigurationInput: React.FC<ConfigurationInputProps> = ({
  disabled = false,
  overwritten = false,
  overwrittenIcon = OverwrittenIcon.SLIDERS,
  parentException = false,
  status,
  value,
  onChange = () => {
    /* empty */
  },
  width,
  placeholder,
  customSuffix,
  displayZero = false,
  allowDecimals = true,
  min,
  max,
}) => {
  // Track local editing state and the last external value we synced with
  const [state, setState] = useState<{ localValue: string | null; lastPropValue: number }>(() => ({
    localValue: null,
    lastPropValue: value,
  }));
  const isEditingRef = useRef(false);

  // If the prop value changed externally (not from our own edit), reset local state
  if (!Object.is(state.lastPropValue, value) && state.localValue === null) {
    setState({ localValue: null, lastPropValue: value });
  }

  // Derive the display value from state and props
  let displayValue: string;
  if (state.localValue !== null) {
    // If currently editing, use local value
    displayValue = state.localValue;
  } else {
    // Otherwise derive from prop value
    if (displayZero && !Number.isFinite(value)) {
      displayValue = '0';
    } else {
      displayValue = value.toString();
    }
  }

  const color: CSSProperties['color'] = useMemo(
    () => getColorBasedOnStatusAndOverwrite(status, overwritten, parentException),
    [overwritten, parentException, status],
  );

  const suffix = useMemo(
    () => getIconBasedOnStatusAndOverwrite(status, overwritten, overwrittenIcon),
    [status, overwritten, overwrittenIcon],
  );

  /**
   * Reset the local editing state and call onChange with the final value
   */
  const commitValue = useCallback(
    (finalValue: string) => {
      onChange(finalValue);
      isEditingRef.current = false;
      setState(prev => ({ ...prev, localValue: null }));
    },
    [onChange],
  );

  /**
   * Normalizes and validates a value by parsing it and clamping it to min/max bounds
   * Returns the normalized value as a string
   */
  const normalizeValue = useCallback(
    (valueString: string): string => {
      // Convert empty string to '0'
      const processedValue = valueString === '' ? '0' : valueString;

      // If no min/max constraints and decimals are allowed, return as-is
      if (min === undefined && max === undefined && allowDecimals) {
        return processedValue;
      }

      // Parse the value based on whether decimals are allowed
      let numericValue = allowDecimals ? parseFloat(processedValue) : parseInt(processedValue);

      // Clamp to minimum bound
      if (min !== undefined && numericValue < min) {
        numericValue = min;
      }

      // Clamp to maximum bound
      if (max !== undefined && numericValue > max) {
        numericValue = max;
      }

      return numericValue.toString();
    },
    [allowDecimals, max, min],
  );

  /**
   * Called when the user types in the input field
   */
  const onChangeHandler = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    isEditingRef.current = true;
    setState(prev => ({ ...prev, localValue: e.target.value }));
  }, []);

  /**
   * Called when the value was edited and the user finishes editing (onBlur or Enter)
   * Validates and clamps the value before calling onChange
   */
  const onChangeChecked = useCallback(
    (newValue: string) => {
      const normalizedValue = normalizeValue(newValue);
      commitValue(normalizedValue);
    },
    [normalizeValue, commitValue],
  );

  /**
   * Called when user moves away from the input field
   * If the value was being edited, we trigger onChangeChecked
   */
  const onBlurHandler = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (isEditingRef.current) {
        onChangeChecked(event.currentTarget.value);
      }
    },
    [onChangeChecked],
  );

  /**
   * Called when the user presses Enter in the input field
   * If the value was being edited, we trigger onChangeChecked
   */
  const onEnterHandler = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (isEditingRef.current) {
        onChangeChecked(event.currentTarget.value);
      }
    },
    [onChangeChecked],
  );

  // Show placeholder for zero value if placeholder is provided
  let effectiveInputValue: string | number;
  if (displayValue === '0' && placeholder) {
    effectiveInputValue = '';
  } else if (!allowDecimals) {
    effectiveInputValue = parseInt(displayValue);
  } else {
    effectiveInputValue = displayValue;
  }

  return (
    <StyledInput
      $color={color}
      suffix={suffix ?? customSuffix}
      type="number"
      disabled={status === ConfigurationFieldStatus.LOADING || disabled}
      value={effectiveInputValue}
      onChange={onChangeHandler}
      onBlur={onBlurHandler}
      onPressEnter={onEnterHandler}
      $width={width}
      placeholder={placeholder}
      size="small"
      min={min}
      max={max}
    />
  );
};
